import {
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
} from '@mui/material';
import { PageLayout } from '../../../shared/layouts';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmNavigationHook } from '@gvdasa/components-confirm';
import { ApiException } from '../../../shared/services/api/ApiException';
import { IntegracaoPayload } from '../../../shared/services/api/c4-model/SistemasService';
import { confirm, feedback } from '../../../shared/services';
import { FerramentasDeDetalhe, Section } from '../../../shared/components';
import { schemaBuilder } from '../../../shared/services/form-validation/Yup';
import { useFormik } from 'formik';
import {
  convertEnumContainerTipo,
  convertBackendDataToFrontend,
} from '../../../shared/utils/enumConverters';
import { EnumContainerTipo } from '../../../shared/enums';
import {
  C4ModelSistemasService,
  C4SistemasPayload,
  C4UsuariosSistema,
  Container,
  Integracao,
  IntegracaoTerceiro,
  BancoDeDados,
} from '../../../shared/services/api/c4-model/SistemasService';
import {
  C4ModelEquipesService,
  Equipes,
} from '../../../shared/services/api/c4-model/EquipesService';
import { useDebounce } from '../../../shared/hooks';
import { ListaUsuarios } from './components/usuarios/ListaUsuarios';
import { ListaContainers } from './components/containers/ListaContainers';
import { ListaIntegracoes } from './components/integracoes/ListaIntegracoes';
import { ListaIntegracaoTerceiros } from './components/integracoes-terceiros/ListaIntegracoesTerceiros';
import { ListaBancoDeDados } from './components/banco-de-dados/ListaBancoDeDados';
import { ListaMensageria } from './components/mensageria/ListaMensageria';
import { ListaCamadas } from './components/camadas/ListaCamadas';

const validationSchema = schemaBuilder.object().shape({
  nome: schemaBuilder.string().required().min(3),
  descricao: schemaBuilder.string().required().min(3),
});

type MensageriaInput = {
  id?: string;
  nomeTopico: string;
  acessoContainers?: Array<{
    id?: string;
    mensageriaId?: string;
    tipo: number;
    direcao: any;
  }>;
};

type Mensageria = {
  id: string;
  sistemaId: string;
  nomeTopico: string;
  acessoContainers: Array<{
    id?: string;
    mensageriaId?: string;
    tipo: number;
    direcao: any;
  }>;
};

type CamadaSimples = {
  id: string;
  nome: string;
  containerId: string;
  containerNome?: string;
  acessoComponentes?: Array<{
    id: string;
    nome: string;
    acessoComponentes?: Array<{
      id: string;
      componente: string;
    }>;
    acessoSistemas?: Array<{
      id: string;
      sistema: string;
      sistemaId?: string;
    }>;
  }>;
};

export const DetalheSistemas: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const { debounce } = useDebounce();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [saveAndNew, setSaveAndNew] = useState(false);
  const [equipes, setEquipes] = useState<Equipes[]>([]);
  const [usuariosSistema, setUsuariosSistema] = useState<C4UsuariosSistema[]>(
    []
  );
  const [containers, setContainers] = useState<Container[]>([]);
  // Armazena as integrações e o id do sistema selecionado para cada integração
  const [integracoes, setIntegracoes] = useState<Integracao[]>([]);
  // Mapeia o índice da integração para o sistemaIntegracaoId selecionado
  const [integracoesSistemaIds, setIntegracoesSistemaIds] = useState<{ [index: number]: string }>({});
  const [integracoesTerceiro, setIntegracoesTerceiro] = useState<
    IntegracaoTerceiro[]
  >([]);
  const [bancosDeDados, setBancosDeDados] = useState<BancoDeDados[]>([]);
  const [mensagerias, setMensagerias] = useState<Mensageria[]>([]);
  const [camadas, setCamadas] = useState<CamadaSimples[]>([]);
  const gvConfirmation = new ConfirmNavigationHook({});

  const handleMensageriasChange = (mensageriasInput: MensageriaInput[]) => {
    const convertedMensagerias: Mensageria[] = mensageriasInput.map((msg) => ({
      id: msg.id || String(Date.now()),
      sistemaId: id === 'novo' ? '' : id,
      nomeTopico: msg.nomeTopico,
      acessoContainers: msg.acessoContainers || [],
    }));
    setMensagerias(convertedMensagerias);
  };


  // Atualiza o sistemaIntegracaoId de uma integração específica
  const handleIntegracaoSistemaIdChange = (index: number, sistemaId: string) => {
    setIntegracoesSistemaIds((prev) => ({ ...prev, [index]: sistemaId }));
  };

  const handleCamadasChange = (camadasInput: CamadaSimples[]) => {
    setCamadas(camadasInput);
  };

  const formik = useFormik<{
    nome: string;
    descricao: string;
    equipe: string;
  }>({
    initialValues: {
      nome: '',
      descricao: '',
      equipe: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setSaveAndNew(false);
      feedback('Salvando...', 'loading');

      // Construir objeto completo com todos os dados
      const sistemaData: C4SistemasPayload = {
        ...values,
        id: id === 'novo' ? '' : id,
        equipeId: values.equipe,
        usuariosSistema: usuariosSistema,
        containers: containers,
        integracoes: (integracoes || []).map((it, idx): IntegracaoPayload => {
          // helper para garantir nomes string para containers
          const containerTypeToName = (val: any) => {
            if (val === null || val === undefined) return '';
            if (typeof val === 'string') {
              if (isNaN(Number(val))) return val;
              const num = Number(val);
              return EnumContainerTipo[num] || String(val);
            }
            if (typeof val === 'number') {
              return EnumContainerTipo[val] || String(val);
            }
            return String(val);
          };

          const integracaoPayload: any = {
            tipoIntegracao: it.tipoIntegracao || '',
            descricao: it.descricao || '',
            acessoContainers: (it.acessoContainers || []).map((ac) => ({
              containerOrigem: containerTypeToName(ac.containerOrigem),
              containerDestino: containerTypeToName(ac.containerDestino),
              acao: ac.acao || '',
              meioAcesso: ac.meioAcesso || '',
              acessoComponentes: (ac.acessoComponentes || []).map((c: any) => ({ nome: c.nome })),
            })),
          };
          // Prioriza o sistemaIntegracaoId selecionado no select, se houver
          const sistemaIdSelecionado = integracoesSistemaIds[idx] || it.sistemaIntegracaoId;
          if (sistemaIdSelecionado) {
            integracaoPayload.sistemaIntegracaoId = sistemaIdSelecionado;
          }
          return integracaoPayload;
        }),
        // Converter IntegracaoTerceiro para payload sem ID
        integracoesTerceiro: integracoesTerceiro.map((it) => ({
          nomeSistema: it.nomeSistema,
          sistemaIntegracaoId: id === 'novo' ? '' : id, // Usar o ID do sistema atual
          descricao: it.descricao,
          acessoContainers: (it.acessoContainers || []).map((ac) => ({
            container: ac.container,
            acao: ac.acao,
            meioAcesso: ac.meioAcesso,
          })),
        })),
        bancosDeDados: bancosDeDados,
        mensagerias: mensagerias.map((msg) => ({
          ...msg,
          acessoContainers: msg.acessoContainers.map((ac) => ({
            id: ac.id || String(Date.now()),
            mensageriaId: ac.mensageriaId || msg.id,
            tipo: ac.tipo,
            direcao: ac.direcao,
          })),
        })),
        camadas: camadas.map((camada) => {
          // Encontrar o nome do container baseado no containerId
          const containerName =
            camada.containerNome ||
            Object.entries(EnumContainerTipo).find(
              ([key, value]) =>
                String(value) === camada.containerId &&
                typeof value === 'number'
            )?.[0] ||
            'FRONTEND';

          return {
            id: camada.id,
            sistemaId: id === 'novo' ? '' : id,
            nome: camada.nome,
            container: containerName, // Enviar o nome como string
            sistema: '',
            componentes: (camada.acessoComponentes || []).map((comp) => ({
              id: comp.id,
              camadaId: camada.id,
              nome: comp.nome,
              camada: '',
              acessoComponentes: (comp.acessoComponentes || []).map((ac) => ({
                id: ac.id,
                componenteId: comp.id,
                nome: ac.componente,
              })),
              acessoSistemas: (comp.acessoSistemas || [])
                .filter((as) => as.sistemaId && as.sistemaId.trim())
                .map((as) => ({
                  sistemaId: as.sistemaId,
                })),
            })),
          };
        }) as any,
        equipe: { id: values.equipe, nome: '' },
      };

      if (id === 'novo') {
        C4ModelSistemasService.create(sistemaData)
          .then(() => {
            feedback('Sistema criado com sucesso', 'success');
            if (saveAndNew) {
              formik.resetForm();
              navigate('/c4-sistemas/detalhe/novo');
            } else {
              navigate('/c4-sistemas');
            }
          })
          .catch(error => {
            if (error instanceof ApiException) {
              feedback(error.message, 'error');
            }
          });
      } else {
        C4ModelSistemasService.updateById(id, sistemaData)
          .then(() => {
            feedback('Sistema atualizado com sucesso', 'success');
            if (saveAndNew) {
              formik.resetForm();
              navigate('/c4-sistemas/detalhe/novo');
            } else {
              navigate('/c4-sistemas');
            }
          })
          .catch(error => {
            if (error instanceof ApiException) {
              feedback(error.message, 'error');
            }
          });
      }
      setIsLoading(false);
    },
  });

  useEffect(() => {
    debounce(() => {
      C4ModelEquipesService.getAllEquipes('1', 100, 0, '', 'asc', 'nome').then(
        (data) => {
          setIsLoading(false);
          if (data instanceof ApiException) {
            feedback(data.message, 'error');
          } else {
            setEquipes(data.data);
          }
        }
      );
    });
  }, [debounce]);

  useEffect(() => {
    if (id === 'novo') {
      setIsLoading(false);
    } else {
      C4ModelSistemasService.getById(id)
        .then((data) => {
          // Converter dados do backend usando a função utilitária
          const convertedData = convertBackendDataToFrontend(data);

          formik.setValues({
            nome: data.nome,
            descricao: data.descricao,
            equipe: data.equipe.id,
          });

          setUsuariosSistema(convertedData.usuariosSistema || []);
          setContainers(convertedData.containers || []);
          setIntegracoes(convertedData.integracoes || []);
          setIntegracoesTerceiro(convertedData.integracoesTerceiro || []);
          setBancosDeDados(convertedData.bancosDeDados || []);
          setMensagerias(convertedData.mensagerias || []);

          // Aguardar os containers serem carregados para converter as camadas
          setTimeout(() => {
            setCamadas(
              (data.camadas || []).map((camada: any) => ({
                id: camada.id,
                nome: camada.nome,
                containerId:
                  convertedData.containers?.find(
                    (c: any) =>
                      c.tipo === convertEnumContainerTipo(camada.container)
                  )?.id || '',
                containerNome: String(camada.container),
                acessoComponentes: (camada.componentes || []).map(
                  (comp: any) => ({
                    id: comp.id,
                    nome: comp.nome,
                    acessoComponentes: (comp.acessoComponentes || []).map(
                      (ac: any) => ({
                        id: ac.id,
                        componente: ac.nome,
                      })
                    ),
                    acessoSistemas: (comp.acessoSistemas || []).map(
                      (as: any) => ({
                        id: as.id,
                        sistemaId: as.sistemaId,
                        sistema: as.sistema?.nome || '',
                      })
                    ),
                  })
                ),
              }))
            );
          }, 0);
        })
        .catch((error) => {
          if (error instanceof ApiException) {
            feedback(error.message, 'error');
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id]);

  useEffect(() => {
    gvConfirmation.componentDidMount();

    return () => {
      gvConfirmation.componentWillUnmount();
    };
  }, []);

  const handleNovo = () => {
    formik.resetForm();
    setUsuariosSistema([]);
    setContainers([]);
    setIntegracoes([]);
    setIntegracoesTerceiro([]);
    setBancosDeDados([]);
    setMensagerias([]);
    setCamadas([]);
    navigate('/c4-sistemas/detalhe/novo');
  };

  const handleSaveAndNew = () => {
    setSaveAndNew(true);
    formik.submitForm();
  };

  const handleDelete = useCallback(() => {
    const handleConfirmDelete = () => {
      feedback('Excluindo...!', 'loading');
      C4ModelSistemasService.deleteById(id)
        .then(() => {
          feedback(`${formik.values.nome} Excluído com sucesso!`, 'success');
          navigate('/c4-sistemas');

        })
        .catch((error) => {
          if (error instanceof ApiException) {
            feedback(error.message, 'error');
          }
        });
    };
    confirm(
      `Tem certeza que deseja excluir ${formik.values.nome}?`,
      'delete',
      handleConfirmDelete
    );
  }, [navigate, id, formik.values.nome]);

  return (
    <PageLayout
      title={id === 'novo' ? 'Novo sistema' : formik.values.nome}
      tools={
        <FerramentasDeDetalhe
          mostrarBotaoDeSalvar
          // desabilitarBotaoApagar={permitirExcluirTermoPadrao}
          // desabilitarBotaoDeSalvar={permitirEditarTermoPadrao}
          mostrarBotaoDeVoltar
          mostrarBotaoDeSalvarENovo={id == 'novo'}
          isBotaoDeNovoLoading={isLoading}
          isBotaoDeApagarLoading={isLoading}
          isBotaoDeSalvarLoading={isLoading}
          isBotaoDeSalvarENovoLoading={isLoading}
          mostrarBotaoDeNovo={id !== 'novo'}
          mostrarBotaoDeApagar={id !== 'novo'}
          mostrarDivider
          aoClicarEmApagar={handleDelete}
          aoClicarEmVoltar={() => {
            navigate('/c4-sistemas');
          }}
          aoClicarEmSalvar={formik.submitForm}
          aoClicarEmSalvarENovo={handleSaveAndNew}
          aoClicarEmNovo={handleNovo}
        />
      }
    >
      <Box>
        <Section title="Dados gerais" margin>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" alignItems="center" gap={6}>
              {!isLoading && (
                <FormControl>
                  <FormLabel required>Nome</FormLabel>
                  <TextField
                    sx={{ width: '445px' }}
                    variant="outlined"
                    name="nome"
                    value={formik.values.nome}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.nome && Boolean(formik.errors.nome)}
                    helperText={formik.touched.nome && formik.errors.nome}
                  />
                </FormControl>
              )}
              {!isLoading && (
                <FormControl>
                  <FormLabel required>Equipes</FormLabel>
                  <Select
                    sx={{ width: '445px' }}
                    required
                    name="equipe"
                    value={formik.values.equipe}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.equipe && Boolean(formik.errors.equipe)
                    }
                  >
                    {equipes.map((equipe) => (
                      <MenuItem key={equipe.id} value={equipe.id}>
                        {equipe.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {isLoading && (
                <Skeleton
                  variant="rectangular"
                  animation="pulse"
                  height={50}
                  width="445px"
                />
              )}
            </Box>
            <Box>
              {!isLoading && (
                <FormControl>
                  <FormLabel required>Descrição</FormLabel>
                  <TextField
                    sx={{ width: '445px' }}
                    variant="outlined"
                    name="descricao"
                    value={formik.values.descricao}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.descricao &&
                      Boolean(formik.errors.descricao)
                    }
                    helperText={
                      formik.touched.descricao && formik.errors.descricao
                    }
                  />
                </FormControl>
              )}
              {isLoading && (
                <Skeleton
                  variant="rectangular"
                  animation="pulse"
                  height={50}
                  width="445px"
                />
              )}
            </Box>
          </Box>
        </Section>
        <Section title="Usuários" margin>
          <ListaUsuarios
            initialRows={usuariosSistema}
            onUsuariosChange={setUsuariosSistema}
            isLoading={isLoading}
          />
        </Section>
        <Section title="Containers" margin>
          <ListaContainers
            initialRows={containers}
            onContainersChange={setContainers}
            isLoading={isLoading}
          />
        </Section>
        <Section title="Integrações" margin>
          <ListaIntegracoes
            initialRows={integracoes}
            currentSystemId={id === 'novo' ? undefined : id}
            onIntegracoesChange={setIntegracoes}
            isLoading={isLoading}
          />
        </Section>
        {id !== 'novo' && (
          <Section title="Integrações Terceiros" margin>
            <ListaIntegracaoTerceiros
              initialRows={integracoesTerceiro}
              onIntegracoesTerceiroChange={setIntegracoesTerceiro}
              currentSystemId={id}
              isLoading={isLoading}
            />
          </Section>
        )}
        <Section title="Banco de Dados" margin>
          <ListaBancoDeDados
            initialRows={bancosDeDados}
            onBancosDeDadosChange={setBancosDeDados}
            currentSystemId={id === 'novo' ? undefined : id}
            isLoading={isLoading}
          />
        </Section>
        <Section title="Mensageria" margin>
          <ListaMensageria
            initialRows={mensagerias.map((msg) => ({
              id: msg.id,
              nomeTopico: msg.nomeTopico,
              acessoContainers: msg.acessoContainers,
            }))}
            onMensageriasChange={handleMensageriasChange}
            currentSystemId={id === 'novo' ? undefined : id}
            isLoading={isLoading}
          />
        </Section>
        <Section title="Camadas" margin>
          <ListaCamadas
            initialRows={camadas}
            availableContainers={containers.map((container) => ({
              id: container.id,
              tipo: String(container.tipo),
            }))}
            onCamadasChange={handleCamadasChange}
            currentSystemId={id === 'novo' ? undefined : id}
            isLoading={isLoading}
          />
        </Section>
      </Box>
    </PageLayout>
  );
};
